import { useRef, useState, type FormEvent } from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js';
import { useLogin } from 'react-admin';
import { QRCodeSVG } from 'qrcode.react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  COGNITO_MISCONFIGURED_MESSAGE,
  getUserPool,
  isCognitoConfigured,
} from './cognito';

type Step = 'credentials' | 'newPassword' | 'totpSetup' | 'totpCode';

const ISSUER = 'Cashblack Admin';

/** Map raw Cognito errors to friendly copy; fall back to the message. */
const humanError = (err: { name?: string; message?: string }): string => {
  switch (err.name) {
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'CodeMismatchException':
      return 'That code is incorrect. Try the current code from your authenticator app.';
    case 'ExpiredCodeException':
      return 'That code has expired. Enter the current one from your app.';
    case 'InvalidPasswordException':
      return 'Password does not meet the policy (min 12 chars, upper, lower, number, symbol).';
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return err.message ?? 'Something went wrong. Please try again.';
  }
};

const otpauthUri = (email: string, secret: string): string =>
  `otpauth://totp/${encodeURIComponent(ISSUER)}:${encodeURIComponent(email)}` +
  `?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`;

const Centered = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      bgcolor: 'background.default',
    }}
  >
    <Card sx={{ width: '100%', maxWidth: 420 }}>
      <CardContent>
        <Typography variant="h5" component="h1" gutterBottom>
          Cashblack Admin
        </Typography>
        {children}
      </CardContent>
    </Card>
  </Box>
);

/**
 * Custom login screen talking to Cognito directly via the SDK (SRP).
 *
 * Handles the full first-login flow, not just the happy path:
 *  - NEW_PASSWORD_REQUIRED (admin-created users start in force-change-password),
 *  - TOTP enrolment (associate a software token, show a QR code, verify a code),
 *  - SOFTWARE_TOKEN_MFA on subsequent logins (enter the 6-digit code).
 *
 * Token persistence/refresh is handled by the SDK (tokens cached in
 * localStorage; `getSession` refreshes via the refresh token). On success we
 * hand off to React Admin's `login` to perform the redirect (honouring `?next=`).
 */
const LoginPage = () => {
  const login = useLogin();
  const userRef = useRef<CognitoUser | null>(null);

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isCognitoConfigured()) {
    return (
      <Centered>
        <Alert severity="error">{COGNITO_MISCONFIGURED_MESSAGE}</Alert>
      </Centered>
    );
  }

  // Hand the cached session off to React Admin, which redirects (honours ?next=).
  const finish = () => {
    const next =
      new URLSearchParams(window.location.search).get('next') ?? undefined;
    login({}, next).catch((err: Error) => {
      setBusy(false);
      setError(humanError(err));
    });
  };

  const fail = (err: { name?: string; message?: string }) => {
    setBusy(false);
    setError(humanError(err));
  };

  // Shared challenge callbacks for authenticateUser / completeNewPasswordChallenge.
  const authCallbacks = (): IAuthenticationCallback => ({
    onSuccess: () => finish(),
    onFailure: fail,
    newPasswordRequired: () => {
      setBusy(false);
      setError(null);
      setStep('newPassword');
    },
    // MFA required but not yet enrolled (first login): start TOTP association.
    mfaSetup: () => {
      const user = userRef.current;
      if (!user) return;
      user.associateSoftwareToken({
        associateSecretCode: (secret: string) => {
          setTotpSecret(secret);
          setBusy(false);
          setError(null);
          setStep('totpSetup');
        },
        onFailure: fail,
      });
    },
    // Subsequent logins: prompt for the authenticator code.
    totpRequired: () => {
      setBusy(false);
      setError(null);
      setStep('totpCode');
    },
    mfaRequired: () =>
      fail({
        message:
          'SMS MFA is not supported. This pool uses an authenticator app only.',
      }),
  });

  const submitCredentials = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const user = new CognitoUser({ Username: email, Pool: getUserPool() });
    userRef.current = user;
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      authCallbacks(),
    );
  };

  const submitNewPassword = (e: FormEvent) => {
    e.preventDefault();
    const user = userRef.current;
    if (!user) return;
    setBusy(true);
    setError(null);
    user.completeNewPasswordChallenge(newPassword, {}, authCallbacks());
  };

  const submitTotpSetup = (e: FormEvent) => {
    e.preventDefault();
    const user = userRef.current;
    if (!user) return;
    setBusy(true);
    setError(null);
    user.verifySoftwareToken(code, 'Authenticator app', {
      onSuccess: () => finish(),
      onFailure: fail,
    });
  };

  const submitTotpCode = (e: FormEvent) => {
    e.preventDefault();
    const user = userRef.current;
    if (!user) return;
    setBusy(true);
    setError(null);
    user.sendMFACode(code, authCallbacks(), 'SOFTWARE_TOKEN_MFA');
  };

  const Busy = busy ? (
    <CircularProgress size={20} sx={{ ml: 1 }} color="inherit" />
  ) : null;

  return (
    <Centered>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {step === 'credentials' && (
        <form onSubmit={submitCredentials}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Sign in to the admin panel.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={busy} fullWidth>
              Sign in{Busy}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={submitNewPassword}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Set a new password to finish activating your account.
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={busy} fullWidth>
              Set password{Busy}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'totpSetup' && (
        <form onSubmit={submitTotpSetup}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Scan this with an authenticator app (e.g. Google Authenticator,
              1Password), then enter the 6-digit code.
            </Typography>
            {totpSecret && (
              <QRCodeSVG value={otpauthUri(email, totpSecret)} size={160} />
            )}
            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
              Or enter this secret manually: <code>{totpSecret}</code>
            </Typography>
            <TextField
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
              }}
              required
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={busy} fullWidth>
              Verify & enable{Busy}
            </Button>
          </Stack>
        </form>
      )}

      {step === 'totpCode' && (
        <form onSubmit={submitTotpCode}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
              }}
              required
              fullWidth
              autoFocus
            />
            <Button type="submit" variant="contained" disabled={busy} fullWidth>
              Verify{Busy}
            </Button>
          </Stack>
        </form>
      )}
    </Centered>
  );
};

export default LoginPage;
